import { S } from '@/app/utilities';
import dayjs from 'dayjs';
import { isNil } from 'ramda';
import { match } from 'ts-pattern';
import { MetricField } from '../../asset/types/data-profiling.type';

import customParseFormat from 'dayjs/plugin/customParseFormat';

import utc from 'dayjs/plugin/utc';
dayjs.extend(customParseFormat);
dayjs.extend(utc);

export function useAssetColumnMetrics() {
    const numericalMetrics: MetricField[] = [
        {
            key: 'minValue',
            label: 'Minimum',
            description: 'Lowest value in the column',
            valueDescription: null,
        },
        {
            key: 'maxValue',
            label: 'Maximum',
            description: 'Highest value in the column',
            valueDescription: null,
        },
        {
            key: 'range',
            label: 'Range',
            description: 'Difference between max and min values',
            valueDescription: null,
        },
        {
            key: 'mean',
            label: 'Mean',
            description: 'Average of all values',
            valueDescription: null,
        },
        {
            key: 'variance',
            label: 'Variance',
            description: 'Spread of values',
            valueDescription: null,
        },
        {
            key: 'stdDev',
            label: 'Standard Deviation',
            description: 'Spread around mean',
            valueDescription: null,
        },
        {
            key: 'numOfMissingValues',
            label: 'Missing values',
            description: 'Number of null entries',
            valueDescription: null,
        },
        {
            key: 'numOfNegativeValues',
            label: 'Negative values',
            description: 'Number of negative entries',
            valueDescription: null,
        },
        {
            key: 'numOfZeroValues',
            label: 'Zero values',
            description: 'Number of entries equal to zero',
            valueDescription: null,
        },
    ];

    const datetimeMetrics: MetricField[] = [
        {
            key: 'startingPoint',
            label: 'Starting point',
            description: 'Earliest date/time in the column',
            valueDescription: null,
        },
        {
            key: 'endingPoint',
            label: 'Ending point',
            description: 'Latest date/time in the column',
            valueDescription: null,
        },
        {
            key: 'duration',
            label: 'Duration',
            description: 'Time span between start and end date/time',
            valueDescription: null,
        },
        {
            key: 'numOfMissingValues',
            label: 'Missing values',
            description: 'Number of null entries',
            valueDescription: null,
        },
    ];

    const categoricalMetrics: MetricField[] = [
        {
            key: 'numOfMissingValues',
            label: 'Missing values',
            description: 'Number of null entries',
            valueDescription: null,
        },
    ];

    const getDatetimeText = (dateString: string, type: string) => {
        if (isNil(dateString)) return dateString;
        return match(type)
            .with('datetime', () => `${dayjs.utc(dateString).format('DD/MM/YYYY HH:mm:ss')}`)
            .with('date', () => `${dayjs(dateString).format('DD/MM/YYYY')}`)
            .with('time', () => dayjs(dateString, 'HH:mm:ss').format('HH:mm:ss'))
            .otherwise(() => '');
    };

    const valueNotPrecise = (value: number) => !Number.isInteger(value) && Math.abs(value) < 0.01;

    const valueLargerThan8Digits = (value: number) => Math.abs(value) >= 1e8;

    const getMetricDisplayValue = (metrics: any, key: string) => {
        if (key === 'numOfMissingValues') {
            return `${metrics.numOfMissingValues} (${
                valueNotPrecise(metrics.percentageOfMissingValues * 100) ? '≈' : ''
            }${parseFloat((metrics.percentageOfMissingValues * 100).toFixed(2))}%)`;
        }
        if (key === 'numOfZeroValues') {
            return `${metrics.numOfZeroValues} (${
                valueNotPrecise(metrics.percentageOfZeroValues * 100) ? '≈' : ''
            }${parseFloat((metrics.percentageOfZeroValues * 100).toFixed(2))}%)`;
        }

        if (key === 'numOfNegativeValues') {
            return `${metrics.numOfNegativeValues} (${
                valueNotPrecise(metrics.percentageOfNegativeValues * 100) ? '≈' : ''
            }${parseFloat((metrics.percentageOfNegativeValues * 100).toFixed(2))}%)`;
        }

        if (key === 'duration') return S.formatDurationInSeconds(metrics.duration, metrics.type);

        if (typeof metrics[key] === 'number' && valueLargerThan8Digits(metrics[key]))
            return metrics[key].toExponential(2);

        if (typeof metrics[key] === 'number')
            return `${valueNotPrecise(metrics[key]) ? '≈' : ''}${parseFloat(metrics[key].toFixed(2))}`;

        if (key === 'startingPoint' || key === 'endingPoint') return getDatetimeText(metrics[key], metrics.type);

        return metrics[key];
    };

    const getValueDescription = (metrics: any, key: string) => {
        if (key === 'numOfMissingValues' && !Number.isInteger(metrics.percentageOfMissingValues * 100))
            return (metrics.percentageOfMissingValues * 100).toString();
        if (key === 'numOfZeroValues' && !Number.isInteger(metrics.percentageOfZeroValues * 100))
            return (metrics.percentageOfZeroValues * 100).toString();
        if (key === 'numOfNegativeValues' && !Number.isInteger(metrics.percentageOfNegativeValues * 100))
            return (metrics.percentageOfNegativeValues * 100).toString();

        if (
            typeof metrics[key] === 'number' &&
            (!Number.isInteger(metrics[key]) || valueLargerThan8Digits(metrics[key]))
        )
            return metrics[key].toString();

        return null;
    };

    const getValueNotPrecise = (metrics: any, key: string) =>
        !Number.isInteger(metrics[key]) && Math.abs(metrics[key]) < 0.01;

    return {
        numericalMetrics,
        datetimeMetrics,
        categoricalMetrics,
        getMetricDisplayValue,
        getValueDescription,
        getValueNotPrecise,
    };
}
